// Operator lane selection from the launch URL.
//
// A launch URL may carry an optional first path segment naming which of the
// operator's own environments the session should talk to:
//
//   https://host/game?clientToken=...       -> default lane (unchanged)
//   https://host/uat/game?clientToken=...   -> the operator's UAT environment
//
// A path segment rather than a query param on purpose: the operator configures
// the launch URL on their side, and a path segment survives their URL handling
// intact, whereas a query param can be dropped or reordered.
//
// The segment feeds two things: the router `basename` (so every route below it
// stays inside the lane) and the `lane` field on the initSession request (so the
// backend binds the session to that operator environment). The backend registry
// in rivalz-games-services `src/providers/lanes.ts` is the source of truth —
// keep this list in step with it.

export const LANE_SEGMENTS = ['uat', 'test'];

/** Build-time base path the app is served under (`/` in every current build). */
export function configuredBase() {
  return process.env.REACT_APP_HOME_PAGE || '/';
}

function stripTrailingSlash(value) {
  return value.length > 1 && value.endsWith('/') ? value.slice(0, -1) : value;
}

/**
 * The lane segment in `pathname`, or null when there isn't one.
 *
 * Matches against the known segments rather than just taking the first one:
 * a bare `/results` refresh must not be read as a lane named "results", which
 * would set the router basename to `/results` and 404 the whole app.
 *
 * Matching is case-sensitive to stay consistent with React Router's `basename`,
 * so `/UAT/game` deliberately does NOT resolve — give operators the exact
 * lowercase URL.
 */
export function detectLane(pathname, base = configuredBase()) {
  const prefix = stripTrailingSlash(base);
  let rest = pathname;
  if (prefix !== '/') {
    // A path outside our configured base isn't ours to interpret. Fall back to
    // the default lane rather than reading a segment that happens to share a
    // lane's name.
    if (!rest.startsWith(prefix)) return null;
    rest = rest.slice(prefix.length);
  }
  const [first] = rest.split('/').filter(Boolean);
  return LANE_SEGMENTS.includes(first) ? first : null;
}

/**
 * The React Router `basename` for this page load: the configured base plus the
 * lane segment, if any.
 *
 * Putting the lane in `basename` rather than in the route table means every
 * existing route, link and redirect keeps working unchanged and automatically
 * stays inside the lane — including the `/game` -> `/game/:gameId` launch
 * redirect and the catch-all, which would otherwise eject the player out of the
 * lane on their first navigation.
 */
export function routerBasename(pathname, base = configuredBase()) {
  const prefix = stripTrailingSlash(base);
  const lane = detectLane(pathname, base);
  if (!lane) return base;
  return `${prefix === '/' ? '' : prefix}/${lane}`;
}
